import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Empty, Input, Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import debounce from "lodash/debounce";
import { buildColumnsWithHeaderFilters } from "../../FilterBar/FilterBar";
import styles from "./LookupTableSelect.module.scss";

type LookupPath = string | number | Array<string | number>;

export type LookupTableOption = {
  label?: React.ReactNode;
  value?: string;
  raw?: Record<string, any>;
  [key: string]: any;
};

export type LookupTableSelectProps<ValueType extends LookupTableOption = any> = {
  value?: ValueType | string | null;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  style?: React.CSSProperties;
  popupWidth?: number;
  debounceTimeout?: number;
  fetchOptions: (
    search: string,
    value?: string,
    filters?: Record<string, any>,
  ) => Promise<ValueType[]>;
  tableColumns?: ColumnsType<ValueType>;
  rowKey?: string | ((record: ValueType) => React.Key);
  onChange?: (value: ValueType | undefined) => void;
};

const toPath = (path: LookupPath): Array<string | number> =>
  Array.isArray(path) ? path : [path];

const getByPath = (source: any, path: LookupPath) =>
  toPath(path).reduce((value, key) => (value == null ? value : value[key]), source);

const normalizeText = (value: any) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
};

export const getLookupOptionLabel = (option?: LookupTableOption | string | null) => {
  if (!option) return "";
  if (typeof option === "string") return option;

  return normalizeText(option.label ?? option.name ?? option.raw?.name);
};

export const getLookupOptionValue = (option?: LookupTableOption | string | null) => {
  if (!option) return undefined;
  if (typeof option === "string") return option;

  return option.value ?? option._id ?? option.raw?._id;
};

export const getLookupOptionCellText = (
  option: LookupTableOption,
  dataIndex: LookupPath,
) => {
  const optionValue = getByPath(option, dataIndex);
  if (optionValue !== undefined && optionValue !== null) {
    return normalizeText(optionValue);
  }

  return normalizeText(getByPath(option.raw, dataIndex));
};

export const getLookupPopupWidthStyle = (
  popupWidth: number,
): React.CSSProperties => ({
  width: `min(${popupWidth}px, calc(100vw - 48px))`,
  maxWidth: "calc(100vw - 48px)",
});

export const getLookupPopupPositionStyle = ({
  triggerTop,
  triggerBottom,
  popupWidth,
  popupHeight,
  viewportWidth,
  viewportHeight,
  viewportMargin = 24,
  offsetY = 8,
}: {
  triggerTop?: number;
  triggerBottom: number;
  popupWidth: number;
  popupHeight?: number;
  viewportWidth: number;
  viewportHeight?: number;
  viewportMargin?: number;
  offsetY?: number;
}): React.CSSProperties => {
  const effectiveWidth = Math.min(
    popupWidth,
    Math.max(viewportWidth - viewportMargin * 2, 0),
  );
  const baseStyle: React.CSSProperties = {
    left: Math.max(viewportMargin, Math.round((viewportWidth - effectiveWidth) / 2)),
    position: "fixed",
    top: triggerBottom + offsetY,
    zIndex: 1050,
  };

  if (
    triggerTop === undefined ||
    viewportHeight === undefined ||
    popupHeight === undefined
  ) {
    return baseStyle;
  }

  const spaceBelow = Math.max(
    viewportHeight - triggerBottom - viewportMargin - offsetY,
    0,
  );
  const spaceAbove = Math.max(triggerTop - viewportMargin - offsetY, 0);
  const shouldOpenAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;
  const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow;
  const maxHeight = Math.min(popupHeight, Math.max(availableHeight, 180));
  const top = shouldOpenAbove
    ? Math.max(viewportMargin, triggerTop - offsetY - maxHeight)
    : triggerBottom + offsetY;

  return {
    ...baseStyle,
    maxHeight,
    top,
  };
};

const defaultColumns: ColumnsType<LookupTableOption> = [
  {
    title: "Mã",
    dataIndex: "code",
    width: 120,
    render: (_value, record) => getLookupOptionCellText(record, "code"),
  },
  {
    title: "Tên",
    dataIndex: "label",
    width: 240,
    render: (_value, record) => getLookupOptionLabel(record),
  },
  {
    title: "MST",
    dataIndex: "taxNumber",
    width: 140,
    render: (_value, record) =>
      getLookupOptionCellText(record, "taxNumber") ||
      getLookupOptionCellText(record, "taxCode"),
  },
  {
    title: "SĐT",
    dataIndex: "phoneNumber",
    width: 140,
    render: (_value, record) =>
      getLookupOptionCellText(record, "phoneNumber") ||
      getLookupOptionCellText(record, ["contact", "phoneNumber"]),
  },
  {
    title: "Địa chỉ",
    dataIndex: "address",
    width: 280,
    render: (_value, record) => getLookupOptionCellText(record, "address"),
  },
];

const DEFAULT_POPUP_HEIGHT = 380;
const POPUP_TABLE_VERTICAL_OFFSET = 90;

export const LookupTableSelect = <ValueType extends LookupTableOption = any>({
  value,
  placeholder = "Chọn dữ liệu",
  searchPlaceholder = "Tìm kiếm",
  disabled,
  allowClear,
  style,
  popupWidth = 820,
  debounceTimeout = 500,
  fetchOptions,
  tableColumns,
  rowKey,
  onChange,
}: LookupTableSelectProps<ValueType>) => {
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [lookupFilters, setLookupFilters] = useState<Record<string, any>>({});
  const fetchRef = useRef(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const selectedValue = getLookupOptionValue(value);
  const selectedLabel = getLookupOptionLabel(value);
  const activeTableColumns = (tableColumns ||
    defaultColumns) as ColumnsType<ValueType>;
  const filteredTableColumns = useMemo(
    () =>
      buildColumnsWithHeaderFilters(
        activeTableColumns as any,
        lookupFilters,
        setLookupFilters,
        {
          popoverProps: {
            getPopupContainer: () => popupRef.current || document.body,
            zIndex: 1060,
          },
        },
      ) as ColumnsType<ValueType>,
    [activeTableColumns, lookupFilters],
  );
  const popupWidthStyle = getLookupPopupWidthStyle(popupWidth);
  const totalColumnWidth = activeTableColumns.reduce((total, column) => {
    const columnWidth = Number(column.width);
    return total + (Number.isFinite(columnWidth) ? columnWidth : 0);
  }, 0);
  const tableScrollX = Math.max(totalColumnWidth, popupWidth - 8);
  const [popupPositionStyle, setPopupPositionStyle] =
    useState<React.CSSProperties>(() =>
      getLookupPopupPositionStyle({
        triggerBottom: 0,
        popupWidth,
        viewportWidth:
          typeof window === "undefined" ? popupWidth + 48 : window.innerWidth,
      }),
    );
  const popupMaxHeight = Number(popupPositionStyle.maxHeight);
  const tableScrollY = Number.isFinite(popupMaxHeight)
    ? Math.max(160, popupMaxHeight - POPUP_TABLE_VERTICAL_OFFSET)
    : 280;

  const updatePopupPosition = useCallback(() => {
    if (typeof window === "undefined") return;

    const triggerRect = triggerRef.current?.getBoundingClientRect();
    if (!triggerRect) return;

    setPopupPositionStyle(
      getLookupPopupPositionStyle({
        triggerTop: triggerRect.top,
        triggerBottom: triggerRect.bottom,
        popupWidth,
        popupHeight:
          popupRef.current?.getBoundingClientRect().height ||
          DEFAULT_POPUP_HEIGHT,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }),
    );
  }, [popupWidth]);

  const loadOptions = useMemo(
    () =>
      debounce((search: string) => {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setFetching(true);

        fetchOptions(search, selectedValue, lookupFilters).then((nextOptions) => {
          if (fetchId !== fetchRef.current) return;
          setOptions(nextOptions ?? []);
          setFetching(false);
        });
      }, debounceTimeout),
    [debounceTimeout, fetchOptions, lookupFilters, selectedValue],
  );

  useEffect(
    () => () => {
      loadOptions.cancel();
    },
    [loadOptions],
  );

  useEffect(() => {
    if (open) {
      loadOptions(searchText);
    }
  }, [loadOptions, open, searchText]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);

    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [open, updatePopupPosition]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popupRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (record: ValueType) => {
    onChange?.(record);
    setOpen(false);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleClear = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.(undefined);
  };

  const getRowKey = (record: ValueType) => {
    if (typeof rowKey === "function") return rowKey(record);
    if (typeof rowKey === "string") return getLookupOptionCellText(record, rowKey);
    return getLookupOptionValue(record) ?? getLookupOptionLabel(record);
  };

  const popupContent =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={popupRef}
            className={styles.PopupShell}
            style={popupPositionStyle}
          >
            <div className={styles.Popup} style={popupWidthStyle}>
              <Input.Search
                className={styles.Search}
                allowClear
                autoFocus
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <Spin spinning={fetching}>
                <Table
                  className={styles.Table}
                  size="small"
                  columns={filteredTableColumns}
                  dataSource={options}
                  rowKey={(record) => getRowKey(record)}
                  pagination={false}
                  locale={{ emptyText: <Empty description="Không có dữ liệu" /> }}
                  scroll={{ x: tableScrollX, y: tableScrollY }}
                  rowClassName={(record) =>
                    String(getLookupOptionValue(record)) === String(selectedValue)
                      ? styles.SelectedRow
                      : ""
                  }
                  onRow={(record) => ({
                    onClick: () => handleSelect(record),
                  })}
                />
              </Spin>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={styles.LookupTableSelect} style={style}>
      <button
        ref={triggerRef}
        type="button"
        className={[
          styles.Trigger,
          open ? styles.TriggerOpen : "",
          disabled ? styles.TriggerDisabled : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled}
        onClick={handleTriggerClick}
      >
        <span
          className={[
            styles.TriggerText,
            !selectedLabel ? styles.Placeholder : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {selectedLabel || placeholder}
        </span>
        <span className={styles.TriggerActions}>
          {allowClear && selectedValue && !disabled ? (
            <span
              className={styles.ClearButton}
              role="button"
              tabIndex={-1}
              aria-label="Xóa lựa chọn"
              onClick={handleClear}
              onMouseDown={(event) => event.preventDefault()}
            >
              ×
            </span>
          ) : null}
          <span>⌄</span>
        </span>
      </button>
      {popupContent}
    </div>
  );
};
